// lib/studio-project-view.ts
import type { KernelHandle, ProjectType, StudioProjectKind, StudioViewMode, UserPermissions } from '#types/types';
import { STUDIO_PROJECT_TYPES } from '#types/types';

// The three Studio tools. A project's kind is fixed at creation and decides its dashboard, its
// sidebar entry, its label on cards and the permission that opens it.
export const STUDIO_PROJECT_KINDS: Record<StudioProjectKind, {
  types: readonly ProjectType[];
  defaultType: ProjectType;
  label: string;
  permission: 'canAccessFactory' | 'canAccessVector' | null;
}> = {
  canvas: { types: ['FREEFORM', 'STORE_SWAP'], defaultType: 'FREEFORM', label: 'Canvas', permission: null },
  list: { types: ['LIST'], defaultType: 'LIST', label: 'List', permission: 'canAccessFactory' },
  vector: { types: ['VECTOR'], defaultType: 'VECTOR', label: 'Vector', permission: 'canAccessVector' },
};

const STUDIO_PROJECT_KIND_NAMES = Object.keys(STUDIO_PROJECT_KINDS) as StudioProjectKind[];

export const isStudioProjectKind = (value: unknown): value is StudioProjectKind =>
  typeof value === 'string' && (STUDIO_PROJECT_KIND_NAMES as string[]).includes(value);

export const isStudioProjectType = (value: unknown): value is ProjectType =>
  typeof value === 'string' && (STUDIO_PROJECT_TYPES as readonly string[]).includes(value);

export const projectKindOf = (projectType: unknown): StudioProjectKind =>
  STUDIO_PROJECT_KIND_NAMES.find(kind => (STUDIO_PROJECT_KINDS[kind].types as readonly unknown[]).includes(projectType)) ?? 'canvas';

export const canAccessStudioKind = (kind: StudioProjectKind, permissions: UserPermissions | null | undefined): boolean => {
  const permission = STUDIO_PROJECT_KINDS[kind].permission;
  return !permission || Boolean(permissions?.[permission]);
};

export const resolveProjectGenerationViewMode = (projectType: unknown): StudioViewMode =>
  projectType === 'LIST' ? 'marketing' : 'studio';

// The two Studio tools no longer expose a cross-project-type view switch. Retire
// only the opened project's obsolete UI override; captured generation scopes and
// other projects' runtime stay intact. Wait for the fixed type to be hydrated.
export const restoreProjectToolView = (
  kernel: Pick<KernelHandle, 'getInventory' | 'readProjectRuntimeState' | 'patchProjectRuntimeState'> & Partial<Pick<KernelHandle, 'hydrateVisibleLayers'>>,
  projectId: string,
): void => {
  const type = kernel.getInventory().Project.registry[projectId]?.projectType;
  if (!isStudioProjectType(type)) return;
  const view = resolveProjectGenerationViewMode(type);
  const previous = kernel.readProjectRuntimeState(projectId).projectViewMode;
  if (previous && previous !== view) setProjectViewMode(kernel, projectId, view);
};


export const setProjectViewMode = (
  kernel: Pick<KernelHandle, 'patchProjectRuntimeState'> & Partial<Pick<KernelHandle, 'hydrateVisibleLayers'>>,
  projectId: string,
  viewMode: StudioViewMode,
): void => {
  const normalizedProjectId = projectId.trim();
  if (!normalizedProjectId) return;
  kernel.patchProjectRuntimeState(normalizedProjectId, { projectViewMode: viewMode });
  if (viewMode === 'studio') kernel.hydrateVisibleLayers?.(normalizedProjectId);
};

export const resolveProjectStudioViewMode = (
  projectType: unknown,
  runtimeViewMode: unknown,
): StudioViewMode => {
  if (runtimeViewMode === 'studio' || runtimeViewMode === 'marketing') {
    return runtimeViewMode;
  }
  return resolveProjectGenerationViewMode(projectType);
};

// The sidebar section a project belongs to. Every "open a project" path reads it here so a kind
// cannot light the wrong tool, and a kind the account cannot open falls back to Canvas rather than
// leaving the dashboard empty. The marketing override only ever applied to Canvas types.
export const resolveProjectStudioSection = (
  projectType: unknown,
  runtimeViewMode: unknown,
  permissions: UserPermissions | null | undefined,
): StudioProjectKind => {
  const kind = projectKindOf(projectType);
  const section = kind === 'canvas' && resolveProjectStudioViewMode(projectType, runtimeViewMode) === 'marketing' ? 'list' : kind;
  return canAccessStudioKind(section, permissions) ? section : 'canvas';
};

export const getProjectStudioViewMode = (
  kernel: Pick<KernelHandle, 'getInventory'>,
  projectId: string | null | undefined,
): StudioViewMode => {
  const project = projectId ? kernel.getInventory().Project.registry[projectId] : undefined;
  return resolveProjectStudioViewMode(project?.projectType, project?.runtime?.projectViewMode);
};
